/**
 * UserListReq 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * UserListReq
 */
export interface UserListReq {
  /** 搜索关键词 */
  q?: string
  /** 是否删除 */
  deleted?: boolean
  /** 页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 排序字段 */
  order_by?: string
  /** 排序方向 */
  order?: string
  /** 创建时间范围 (start,end) */
  created_at__range?: any
  /** 更新时间范围 (start,end) */
  updated_at__range?: any
  /** 更新时间距今(如:7d,1h,30m) */
  updated_at__since?: any
  /** 是否禁用 */
  disabled?: boolean
  /** ID列表，换行符分隔 */
  id__in?: string
  /** 用户列表筛选 */
  filter_preset?: string
  /** 性别 */
  gender?: string
  /** 用户类型 */
  type?: string
  /** 最小段位 */
  rank_tier__gte?: number
  /** 最大段位 */
  rank_tier__lte?: number
  /** 段位大于 */
  rank_tier__gt?: number
  /** 段位小于 */
  rank_tier__lt?: number
  /** 最小积分 */
  rank_points__gte?: number
  /** 最大积分 */
  rank_points__lte?: number
  /** 积分范围 */
  rank_points__range?: number[]
  /** 性别列表 */
  gender__in?: string[]
  /** 类型列表 */
  type__in?: string[]
  /** 用户名包含 */
  username__like?: string
  /** 昵称包含 */
  nickname__like?: string
  /** 简介包含 */
  bio__contains?: string
  /** 用户名包含 */
  username__contains?: string
  /** 昵称包含 */
  nickname__contains?: string
  /** 用户名前缀 */
  username__starts_with?: string
  /** 用户名后缀 */
  username__ends_with?: string
  /** 创建时间距今(如:7d,1h,30m) */
  created_at__since?: any
  /** 创建时间早于(如:7d,1h,30m) */
  created_at__before?: any
  /** 更新时间早于(如:7d,1h,30m) */
  updated_at__before?: any
  /** 联系邮箱 */
  user_contact_email?: string
  /** 联系手机号 */
  user_contact_phone?: string
}

/**
 * UserListReq Schema
 */
export const UserListReqSchema: SchemaDefinition = {
  properties: {
    q: {
      description: '搜索关键词',
      format: 'string',
      type: 'string',
      label: '搜索关键词',
    },
    deleted: {
      description: '是否删除',
      format: '*bool',
      type: 'boolean',
      label: '是否删除',
    },
    page: {
      description: '页码',
      format: 'int',
      type: 'integer',
      label: '页码',
    },
    page_size: {
      description: '每页数量',
      format: 'int',
      type: 'integer',
      label: '每页数量',
    },
    order_by: {
      description: '排序字段',
      format: 'string',
      type: 'string',
      label: '排序字段',
    },
    order: {
      description: '排序方向',
      format: 'string',
      type: 'string',
      label: '排序方向',
    },
    created_at__range: {
      description: '创建时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '创建时间范围',
    },
    updated_at__range: {
      description: '更新时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '更新时间范围',
    },
    updated_at__since: {
      description: '更新时间距今(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '更新时间距今',
    },
    disabled: {
      description: '是否禁用',
      format: '*bool',
      type: 'boolean',
      label: '是否禁用',
    },
    id__in: {
      description: 'ID列表，换行符分隔',
      format: 'string',
      type: 'string',
      label: 'ID列表',
    },
    filter_preset: {
      description: '用户列表筛选',
      format: 'cons.UserListFilterPreset',
      type: 'string',
      label: '用户列表筛选',
    },
    gender: {
      description: '性别',
      format: 'cons.UserGender',
      type: 'string',
      label: '性别',
    },
    type: {
      description: '用户类型',
      format: 'cons.UserType',
      type: 'string',
      label: '用户类型',
    },
    rank_tier__gte: {
      description: '最小段位',
      format: '*int',
      maximum: 7,
      minimum: 0,
      type: 'integer',
      label: '最小段位',
    },
    rank_tier__lte: {
      description: '最大段位',
      format: '*int',
      maximum: 7,
      minimum: 0,
      type: 'integer',
      label: '最大段位',
    },
    rank_tier__gt: {
      description: '段位大于',
      format: '*int',
      maximum: 7,
      minimum: 0,
      type: 'integer',
      label: '段位大于',
    },
    rank_tier__lt: {
      description: '段位小于',
      format: '*int',
      maximum: 7,
      minimum: 0,
      type: 'integer',
      label: '段位小于',
    },
    rank_points__gte: {
      description: '最小积分',
      format: '*int',
      minimum: 0,
      type: 'integer',
      label: '最小积分',
    },
    rank_points__lte: {
      description: '最大积分',
      format: '*int',
      minimum: 0,
      type: 'integer',
      label: '最大积分',
    },
    rank_points__range: {
      description: '积分范围',
      format: '[]int',
      items: {
        format: 'int',
        type: 'integer',
      },
      type: 'array',
      label: '积分范围',
    },
    gender__in: {
      description: '性别列表',
      format: '[]cons.UserGender',
      items: {
        format: 'cons.UserGender',
        type: 'string',
      },
      type: 'array',
      label: '性别列表',
    },
    type__in: {
      description: '类型列表',
      format: '[]cons.UserType',
      items: {
        format: 'cons.UserType',
        type: 'string',
      },
      type: 'array',
      label: '类型列表',
    },
    username__like: {
      description: '用户名包含',
      format: 'string',
      type: 'string',
      label: '用户名包含',
    },
    nickname__like: {
      description: '昵称包含',
      format: 'string',
      type: 'string',
      label: '昵称包含',
    },
    bio__contains: {
      description: '简介包含',
      format: 'string',
      type: 'string',
      label: '简介包含',
    },
    username__contains: {
      description: '用户名包含',
      format: 'string',
      type: 'string',
      label: '用户名包含',
    },
    nickname__contains: {
      description: '昵称包含',
      format: 'string',
      type: 'string',
      label: '昵称包含',
    },
    username__starts_with: {
      description: '用户名前缀',
      format: 'string',
      type: 'string',
      label: '用户名前缀',
    },
    username__ends_with: {
      description: '用户名后缀',
      format: 'string',
      type: 'string',
      label: '用户名后缀',
    },
    created_at__since: {
      description: '创建时间距今(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '创建时间距今',
    },
    created_at__before: {
      description: '创建时间早于(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '创建时间早于',
    },
    updated_at__before: {
      description: '更新时间早于(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '更新时间早于',
    },
    user_contact_email: {
      description: '联系邮箱',
      format: 'string',
      type: 'string',
      label: '联系邮箱',
    },
    user_contact_phone: {
      description: '联系手机号',
      format: 'string',
      type: 'string',
      label: '联系手机号',
    },
  },
  type: 'object',
}
